import React, { useContext, useState, useEffect } from "react"

import {
	Paper,
	Container,
	Grid,
	Typography,
	TextField,
	Button,
	Box,
	CircularProgress,
} from "@material-ui/core"
import Autocomplete from "@material-ui/lab/Autocomplete"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { startOfMonth, format } from "date-fns"
import {
	BackspaceOutlined,
	ConfirmationNumber,
	HourglassEmpty,
	MeetingRoom,
	CheckCircleOutline,
	Memory,
	EmojiPeople,
	AccountBalance,
	AssignmentTurnedIn,
	Search,
} from "@material-ui/icons"

import { AuthContext } from "../../context/Auth/AuthContext"
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext"
import api from "../../services/api"
import PeriodFilter from "../../components/PeriodFilter"

import Chart from "./Chart"
import RoboAnimado from "../../components/RoboAnimado"

// Paleta verde dos cards. O âmbar fica, porque "em espera" e afins precisam
// continuar chamando atenção.
const CORES = {
	escuro: { verde: "#22c55e", verdeClaro: "#4ade80", agua: "#2dd4bf", ambar: "#f5b23d" },
	claro: { verde: "#1E9855", verdeClaro: "#16a34a", agua: "#0d9488", ambar: "#d97706" },
}

const hexParaRgba = (hex, alfa) => {
	const n = parseInt(hex.slice(1), 16)
	return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alfa})`
}

// Número que sobe do zero até o valor. Aceita "61%" / "0.7%" também.
const ContadorAnimado = ({ valor }) => {
	const texto = String(valor)
	const m = texto.match(/^(-?\d+(?:[.,]\d+)?)(.*)$/)
	const alvo = m ? parseFloat(m[1].replace(",", ".")) : null
	const casas = m && /[.,]/.test(m[1]) ? m[1].split(/[.,]/)[1].length : 0
	const sufixo = m ? m[2] : ""
	const [atual, setAtual] = useState(0)

	useEffect(() => {
		if (alvo === null) return undefined
		let frame
		const inicio = performance.now()
		const duracao = 900
		const passo = agora => {
			const k = Math.min((agora - inicio) / duracao, 1)
			setAtual(alvo * (1 - Math.pow(1 - k, 3)))
			if (k < 1) frame = requestAnimationFrame(passo)
		}
		frame = requestAnimationFrame(passo)
		return () => cancelAnimationFrame(frame)
	}, [alvo])

	if (alvo === null) return texto
	return `${atual.toFixed(casas)}${sufixo}`
}

// Onda decorativa no rodapé de cada card
// Onda do rodape dos cartoes: dois periodos lado a lado deslizando devagar
// (o desenho se repete a cada 400, entao o loop nao "pula").
const Onda = ({ cor, atraso = 0 }) => (
	<svg
		viewBox="0 0 800 60"
		preserveAspectRatio="none"
		aria-hidden="true"
		style={{
			position: "absolute", left: 0, bottom: 0, width: "200%", height: 46, pointerEvents: "none",
			animation: `dbOnda 9s linear ${atraso}s infinite`,
		}}
	>
		<path d="M0 34 Q100 14 200 34 T400 34 T600 34 T800 34 L800 60 L0 60 Z" fill={cor} />
	</svg>
)

const getDefaultPeriod = () => ({
	dateFrom: format(startOfMonth(new Date()), "yyyy-MM-dd"),
	dateTo: format(new Date(), "yyyy-MM-dd"),
	label: "Este mês",
})

// Estrelinhas do banner (posicoes fixas: nada de piscar diferente a cada render)
const ESTRELAS_BANNER = [
	{ x: "46%", y: "18%", d: 0, s: 1 }, { x: "62%", y: "70%", d: 1.2, s: 0.7 },
	{ x: "74%", y: "14%", d: 0.6, s: 1.2 }, { x: "90%", y: "82%", d: 2, s: 0.8 },
	{ x: "55%", y: "42%", d: 1.7, s: 0.6 }, { x: "36%", y: "80%", d: 0.9, s: 0.9 },
	{ x: "82%", y: "34%", d: 2.4, s: 0.7 }, { x: "68%", y: "52%", d: 0.3, s: 0.5 },
];

const useStyles = makeStyles(theme => {
	const escuro = theme.palette.type === "dark"

	// fundo dos painéis: escuro = verde-quase-preto; claro = branco
	const painel = escuro
		? {
				backgroundColor: "#141a18",
				backgroundImage: "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0))",
				border: "1px solid rgba(255,255,255,0.07)",
				boxShadow: "0 8px 24px rgba(0,0,0,.35)",
			}
		: {
				backgroundColor: "#ffffff",
				border: "1px solid #e7ece9",
				boxShadow: "0 1px 2px rgba(17,24,20,.04), 0 6px 20px rgba(17,24,20,.05)",
			}

	return {
		// O contentBody do layout e overflow:hidden — as outras paginas resolvem
		// isso pelo MainContainer, que cria a propria area de rolagem. O Dashboard
		// nao usa MainContainer, entao precisa criar a dele aqui: sem isto, tudo
		// que passa da altura da tela fica cortado e nao ha o que rolar.
		pagina: {
			height: "100%",
			overflowY: "auto",
			overflowX: "hidden",
			...theme.scrollbarStyles,
		},
		container: {
			paddingTop: theme.spacing(3),
			paddingBottom: theme.spacing(4),
		},

		// ── Banner "Central de comando" (estilo Multi Tech: azul-noite com
		// verde, nos dois temas) com animacoes leves ──
		hero: {
			position: "relative",
			overflow: "hidden",
			borderRadius: 20,
			padding: "26px 28px",
			marginBottom: theme.spacing(3),
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			flexWrap: "wrap",
			gap: 20,
			minHeight: 210,
			background:
				"radial-gradient(520px 260px at 88% 45%, rgba(34,197,94,.22), transparent 70%)," +
				"radial-gradient(480px 220px at 0% 100%, rgba(34,197,94,.12), transparent 70%)," +
				"linear-gradient(120deg, #111816 0%, #151d1a 55%, #10261d 100%)",
			border: "1px solid rgba(74,222,128,.22)",
			boxShadow: escuro
				? "0 14px 40px rgba(0,0,0,.45), 0 0 0 1px rgba(34,197,94,.05)"
				: "0 14px 34px rgba(15,23,42,.22)",
		},
		heroTexto: { position: "relative", zIndex: 1, animation: "iaSubir .5s ease both" },
		heroPilula: {
			display: "inline-flex",
			alignItems: "center",
			gap: 8,
			padding: "5px 12px",
			borderRadius: 20,
			fontSize: "0.68rem",
			fontWeight: 700,
			letterSpacing: "0.08em",
			color: "#e2e8f0",
			background: "rgba(255,255,255,.06)",
			border: "1px solid rgba(255,255,255,.14)",
			"&::before": {
				content: '""',
				width: 7,
				height: 7,
				borderRadius: "50%",
				background: "#22c55e",
				boxShadow: "0 0 8px #22c55e",
				animation: "sbPontoVivo 1.8s ease-in-out infinite",
			},
		},
		heroTitulo: {
			fontSize: "1.9rem",
			fontWeight: 800,
			color: "#ffffff",
			marginTop: 10,
			lineHeight: 1.15,
			"& span": { color: "#22c55e", textShadow: "0 0 18px rgba(34,197,94,.35)" },
		},
		heroSub: {
			fontSize: "0.88rem",
			color: "#a9b8cc",
			marginTop: 4,
		},
		// planeta verde com anel, flutuando devagar, com uma lua em orbita
		planetaArea: {
			position: "absolute",
			right: 70,
			top: 34,
			width: 140,
			height: 140,
			pointerEvents: "none",
			animation: "iaFlutuar 6s ease-in-out infinite",
			[theme.breakpoints.down("sm")]: { display: "none" },
		},
		planeta: {
			position: "absolute",
			inset: 10,
			borderRadius: "50%",
			background: "radial-gradient(circle at 35% 30%, #86efac 0%, #22c55e 38%, #15803d 70%, #064e2a 100%)",
			boxShadow: "0 0 50px rgba(34,197,94,.45), inset -14px -16px 30px rgba(0,0,0,.35)",
		},
		anel: {
			position: "absolute",
			left: -40,
			top: 56,
			width: 220,
			height: 34,
			borderRadius: "50%",
			border: "2px solid rgba(134,239,172,.55)",
			borderTopColor: "transparent",
			transform: "rotate(-14deg)",
		},
		luaOrbita: {
			position: "absolute",
			inset: -26,
			borderRadius: "50%",
			border: "1px dashed rgba(255,255,255,.12)",
			animation: "sbOrbita 14s linear infinite",
			"&::after": {
				content: '""',
				position: "absolute",
				top: "50%",
				right: -5,
				width: 10,
				height: 10,
				marginTop: -5,
				borderRadius: "50%",
				background: "#ffffff",
				boxShadow: "0 0 12px #ffffff",
			},
		},
		// mascote flutuando ao lado do planeta (como o astronauta do Multi Tech)
		mascote: {
			position: "absolute",
			right: 236,
			top: 92,
			width: 54,
			height: 54,
			zIndex: 1,
			overflow: "visible",
			cursor: "pointer",
			animation: "sbFlutuar 4.5s ease-in-out infinite",
			filter: "drop-shadow(0 6px 14px rgba(0,0,0,.45))",
			[theme.breakpoints.down("md")]: { display: "none" },
		},
		estrela: {
			position: "absolute",
			width: 3,
			height: 3,
			borderRadius: "50%",
			background: "#ffffff",
			pointerEvents: "none",
			animation: "sbPiscarEstrela 3s ease-in-out infinite",
		},
		orbita: {
			position: "absolute",
			right: -70,
			top: -50,
			width: 300,
			height: 300,
			borderRadius: "50%",
			border: "1px dashed rgba(255,255,255,.10)",
			pointerEvents: "none",
		},
		// filtros dentro do banner
		filtros: {
			position: "relative",
			zIndex: 1,
			display: "flex",
			alignItems: "center",
			flexWrap: "wrap",
			gap: 10,
			padding: 10,
			borderRadius: 14,
			// no claro o painel é branco sólido: os campos são brancos com texto
			// escuro e ficavam ilegíveis/transparentes sobre o verde
			background: escuro ? "rgba(0,0,0,.25)" : "#ffffff",
			border: `1px solid ${escuro ? "rgba(255,255,255,.08)" : "transparent"}`,
			boxShadow: escuro ? "none" : "0 6px 18px rgba(0,0,0,.12)",
		},

		// Lista suspensa curta e com rolagem propria: antes ela crescia com a
		// quantidade de atendentes e tomava a tela inteira.
		listaFiltro: {
			maxHeight: 240,
			padding: "4px 0",
			...theme.scrollbarStyles,
		},
		// O popup do Autocomplete nasce com a largura exata do campo. Como o campo
		// e estreito, "Todas as conexoes" quebrava em duas linhas e sobrava aquele
		// vazio embaixo. Aqui ele cresce conforme o maior nome da lista.
		popupFiltro: {
			width: "auto !important",
			minWidth: 210,
		},
		paperFiltro: {
			marginTop: 6,
			borderRadius: 10,
			border: `1px solid ${theme.palette.divider}`,
			boxShadow: escuro ? "0 10px 28px rgba(0,0,0,.45)" : "0 10px 28px rgba(16,24,40,.12)",
		},
		opcaoFiltro: {
			whiteSpace: "nowrap",
			fontSize: "0.875rem",
			minHeight: 36,
			padding: "6px 12px",
			// data-focus e a opcao sob o teclado/mouse; aria-selected e a atual.
			'&[data-focus="true"]': {
				backgroundColor: "rgba(30,152,85,0.08)",
			},
			'&[aria-selected="true"]': {
				backgroundColor: "rgba(30,152,85,0.16)",
				fontWeight: 600,
			},
		},
		semResultado: {
			fontSize: "0.85rem",
			color: theme.palette.text.secondary,
			padding: "10px 12px",
		},
		iconeBusca: {
			fontSize: "1.05rem",
			color: theme.palette.text.secondary,
			marginRight: 2,
		},
		formControl: {
			width: 210,
			"& .MuiOutlinedInput-root": {
				borderRadius: 8,
				fontSize: "0.875rem",
				backgroundColor: `${theme.palette.background.paper} !important`,
				paddingLeft: 10,
			},
			"& .MuiAutocomplete-input": {
				padding: "4px 4px !important",
			},
			// sem seta (forcePopupIcon={false}), o campo nao precisa do recuo que o
			// MUI reserva para ela
			"& .MuiAutocomplete-inputRoot": {
				paddingRight: "10px !important",
			},
			// o placeholder e o valor selecionado: precisa ser legivel, nao um
			// cinza apagado de "campo vazio"
			"& input::placeholder": {
				color: theme.palette.text.primary,
				opacity: 1,
			},
		},
		clearBtn: {
			height: 40,
			borderRadius: 8,
			textTransform: "none",
			fontWeight: 600,
			fontSize: "0.875rem",
			padding: "0 18px",
			color: escuro ? "#cbd5e1" : "#475467",
			backgroundColor: escuro ? "rgba(255,255,255,0.06)" : "#f1f3f5",
			"&:hover": { backgroundColor: escuro ? "rgba(255,255,255,0.12)" : "#e4e7ec" },
		},

		// título de seção com a barrinha verde à esquerda
		tituloSecao: {
			fontSize: "0.74rem",
			fontWeight: 800,
			textTransform: "uppercase",
			letterSpacing: "0.1em",
			color: escuro ? "#cbd5e1" : "#344054",
			margin: "22px 4px 12px",
			display: "flex",
			alignItems: "center",
			gap: 10,
			"&::before": {
				content: '""',
				width: 4,
				height: 16,
				borderRadius: 2,
				background: "#22c55e",
				boxShadow: escuro ? "0 0 8px rgba(34,197,94,.6)" : "none",
			},
		},

		linhaBanco: {
			display: "flex",
			alignItems: "center",
			gap: 12,
			padding: "7px 0",
		},
		nomeBanco: {
			minWidth: 110,
			fontSize: "0.82rem",
			fontWeight: 600,
			textTransform: "capitalize",
		},
		trilhaBarra: {
			flex: 1,
			height: 8,
			borderRadius: 6,
			backgroundColor: "rgba(128,128,128,0.15)",
			overflow: "hidden",
		},
		barra: {
			height: "100%",
			borderRadius: 6,
			background: "linear-gradient(90deg, #1E9855, #2ecc71)",
		},
		valorBanco: {
			minWidth: 34,
			textAlign: "right",
			fontSize: "0.82rem",
			fontWeight: 700,
		},
		volumeBanco: {
			minWidth: 96,
			textAlign: "right",
			fontSize: "0.76rem",
			opacity: 0.65,
			fontWeight: 600,
		},
		cardGrid: {
			marginBottom: theme.spacing(2),
		},

		// ── Cards ──  (--cor e --brilho vêm do style de cada card)
		statCard: {
			...painel,
			position: "relative",
			overflow: "hidden",
			padding: theme.spacing(2.5, 3),
			paddingBottom: theme.spacing(3.5),
			borderRadius: 16,
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(0.5),
			animation: "iaSubir .5s ease backwards",
			transition: "transform .25s ease, box-shadow .25s ease",
			// linha colorida no topo
			"&::before": {
				content: '""',
				position: "absolute",
				top: 0,
				left: 0,
				right: 0,
				height: 3,
				background:
					"linear-gradient(90deg, var(--cor) 0%, var(--cor) 35%, rgba(255,255,255,.85) 50%, var(--cor) 65%, var(--cor) 100%)",
				backgroundSize: "250% 100%",
				animation: "dbLinha 4.5s ease-in-out infinite",
			},
			// brilho suave no canto
			"&::after": {
				content: '""',
				position: "absolute",
				inset: 0,
				background: "radial-gradient(220px 110px at 100% 0%, var(--brilho), transparent 70%)",
				pointerEvents: "none",
			},
			"&:hover $cardIconBox": {
				animation: "dbIconePop .45s ease",
			},
			"&:hover": {
				transform: "translateY(-4px)",
				boxShadow: escuro
					? "0 16px 36px rgba(0,0,0,.5), 0 0 24px var(--brilho)"
					: "0 14px 30px rgba(16,40,28,.12)",
			},
			"& > *": { position: "relative", zIndex: 1 },
		},
		cardHeader: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
		},
		cardTitle: {
			fontSize: "0.72rem",
			fontWeight: 700,
			textTransform: "uppercase",
			letterSpacing: "0.07em",
			color: theme.palette.text.secondary,
		},
		cardIconBox: {
			width: 40,
			height: 40,
			borderRadius: 11,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			// balanco leve e constante (cada cartao num tempo diferente)
			animation: "dbIconeBalanco 4s ease-in-out infinite",
		},
		cardNumber: {
			fontSize: "2.1rem",
			fontWeight: 800,
			lineHeight: 1.1,
			minHeight: 38,
			margin: "4px 0 2px",
			display: "flex",
			alignItems: "center",
			letterSpacing: "-0.01em",
		},
		cardLabel: {
			fontSize: "0.78rem",
			fontWeight: 500,
			color: theme.palette.text.secondary,
		},
		cardFooter: {
			fontSize: "0.75rem",
			color: escuro ? "#64748b" : "#98a2b3",
			marginTop: theme.spacing(0.5),
		},
		// card em destaque (fundo verde cheio), como o "Total"
		cardDestaque: {
			background: "linear-gradient(135deg, #16a34a 0%, #22c55e 100%) !important",
			border: "none !important",
			boxShadow: "0 12px 30px rgba(34,197,94,.3) !important",
			"&::before": { display: "none" },
			// faixa de brilho que atravessa o cartao verde de tempos em tempos
			"&::after": {
				inset: "0 auto 0 0",
				width: "35%",
				background: "linear-gradient(90deg, transparent, rgba(255,255,255,.22), transparent)",
				animation: "sbVarrer 5s ease-in-out infinite",
			},
			"& $cardTitle, & $cardLabel, & $cardFooter": { color: "rgba(255,255,255,.85)" },
		},
		chartPaper: {
			...painel,
			padding: theme.spacing(3),
			borderRadius: 16,
			height: 300,
			display: "flex",
			overflow: "hidden",
			flexDirection: "column",
		},
		rankPaper: {
			...painel,
			padding: theme.spacing(2.5, 3),
			borderRadius: 16,
			maxHeight: 300,
			display: "flex",
			flexDirection: "column",
			overflowY: "auto",
		},
		paperTitle: {
			fontSize: "0.85rem",
			fontWeight: 700,
			color: theme.palette.text.secondary,
			marginBottom: theme.spacing(1.5),
		},
	}
})

const Dashboard = () => {
	const classes = useStyles()
	const escuro = useTheme().palette.type === "dark"
	const P = escuro ? CORES.escuro : CORES.claro

	const { user } = useContext(AuthContext)
	const { whatsApps } = useContext(WhatsAppsContext)

	const [usersList, setUsersList] = useState([])

	const [period, setPeriod] = useState(getDefaultPeriod())
	const [connectionFilter, setConnectionFilter] = useState("all")
	const [userFilter, setUserFilter] = useState("all")
	// O que esta digitado no campo, separado do que esta selecionado. Fica
	// vazio o tempo todo, menos enquanto a pessoa digita: assim o campo mostra
	// o filtro atual como placeholder em vez de texto de verdade — nada para
	// ficar selecionado em azul ao clicar.
	const [buscaConexao, setBuscaConexao] = useState("")
	const [buscaAtendente, setBuscaAtendente] = useState("")

	const [resumo, setResumo] = useState(null)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		const fetchData = async () => {
			try {
				const { data } = await api.get("/users", { params: { pageNumber: 1 } })
				setUsersList(data.users || [])
			} catch (err) {
				console.error(err)
			}
		}
		fetchData()
	}, [])

	useEffect(() => {
		let vivo = true

		const buscar = async () => {
			setLoading(true)
			try {
				const { data } = await api.get("/dashboard/resumo", {
					params: {
						dateFrom: period.dateFrom || undefined,
						dateTo: period.dateTo || undefined,
						userId: userFilter !== "all" ? userFilter : undefined,
						whatsappId: connectionFilter !== "all" ? connectionFilter : undefined,
					},
				})
				if (vivo) setResumo(data)
			} catch (err) {
				console.error(err)
			}
			if (vivo) setLoading(false)
		}

		buscar()
		return () => {
			vivo = false
		}
	}, [period, userFilter, connectionFilter])

	const handleClear = () => {
		setPeriod(getDefaultPeriod())
		setConnectionFilter("all")
		setUserFilter("all")
	}

	const t = resumo?.tickets || {}
	const ia = resumo?.ia || {}
	const bancos = resumo?.bancos || {}
	const temBancos = (bancos.porBanco?.length || 0) > 0

	const tituloGrafico = `Atendimentos · ${period.label || "período"}`

	const cardsAtendimento = [
		{
			title: "Total",
			count: t.total ?? 0,
			label: "Atendimentos",
			footer: `Ativos agora: ${(t.aberto ?? 0) + (t.espera ?? 0)}`,
			color: P.verde,
			icon: ConfirmationNumber,
			destaque: true,
		},
		{
			title: "Em espera",
			count: t.espera ?? 0,
			label: "Atendimentos",
			footer: "Aguardando atendimento",
			color: P.ambar,
			icon: HourglassEmpty,
		},
		{
			title: "Em aberto",
			count: t.aberto ?? 0,
			label: "Atendimentos",
			footer: "Em atendimento",
			color: P.agua,
			icon: MeetingRoom,
		},
		{
			title: "Resolvidos",
			count: t.resolvido ?? 0,
			label: "Atendimentos",
			footer: "Finalizados no período",
			color: P.verdeClaro,
			icon: CheckCircleOutline,
		},
	]

	const cardsIA = [
		{
			title: "Atendidos pela IA",
			count: ia.atendidos ?? 0,
			label: "Conversas",
			footer: "Conversas iniciadas no período",
			color: P.ambar,
			icon: Memory,
		},
		{
			title: "Resolvidos sozinha",
			count: ia.resolvidosPelaIA ?? 0,
			label: "Conversas",
			footer: "Sem precisar de pessoa",
			color: P.verdeClaro,
			icon: AssignmentTurnedIn,
		},
		{
			title: "Precisaram de humano",
			count: ia.precisaramHumano ?? 0,
			label: "Conversas",
			footer: "Transferidas para a equipe",
			color: P.agua,
			icon: EmojiPeople,
		},
		{
			title: "Taxa de resolução",
			count: `${ia.taxaResolucao ?? 0}%`,
			label: "Da IA",
			footer: "Quanto ela fecha sem ajuda",
			color: P.verde,
			icon: CheckCircleOutline,
		},
	]

	const cardsBancos = [
		{
			title: "Consultas",
			count: bancos.consultas ?? 0,
			label: "CPFs consultados",
			footer: "Nos bancos, no período",
			color: P.agua,
			icon: AccountBalance,
		},
		{
			title: "Propostas",
			count: bancos.propostas ?? 0,
			label: "Digitadas",
			footer: "Consulta que virou proposta",
			color: P.verde,
			icon: AssignmentTurnedIn,
		},
		{
			title: "Conversão",
			count: `${bancos.taxaConversao ?? 0}%`,
			label: "Consulta → proposta",
			footer: "Quanto da consulta vira negócio",
			color: P.ambar,
			icon: CheckCircleOutline,
		},
	]

	// Os filtros viraram campos de busca: com dezenas de atendentes, rolar uma
	// lista inteira e pior do que digitar as tres primeiras letras. A opcao
	// "todos" entra como primeiro item da lista, no lugar do MenuItem fixo.
	const opcoesConexao = [
		{ id: "all", name: "Todas as conexões" },
		...(whatsApps || []).map(w => ({ id: w.id, name: w.name })),
	]
	const opcoesAtendente = [
		{ id: "all", name: "Todos os atendentes" },
		...(usersList || []).map(u => ({ id: u.id, name: u.name })),
	]
	const acharOpcao = (lista, id) =>
		lista.find(o => String(o.id) === String(id)) || lista[0]

	const primeiroNome = user.name?.split(" ")[0] || "Comandante"

	return (
		<div className={classes.pagina}>
			<Container maxWidth="lg" className={classes.container}>
				{/* Banner "Central de comando" com os filtros */}
				<div className={classes.hero}>
					<div className={classes.orbita} />
					{ESTRELAS_BANNER.map((e, i) => (
						<span
							key={i}
							className={classes.estrela}
							style={{ left: e.x, top: e.y, animationDelay: `${e.d}s`, transform: `scale(${e.s})` }}
						/>
					))}
					<div className={classes.planetaArea}>
						<div className={classes.luaOrbita} />
						<div className={classes.planeta} />
						<div className={classes.anel} />
					</div>
					<RoboAnimado className={classes.mascote} />

					<div className={classes.heroTexto}>
						<span className={classes.heroPilula}>CENTRAL DE COMANDO</span>
						<div className={classes.heroTitulo}>
							Olá, <span>{primeiroNome}</span>
						</div>
						<div className={classes.heroSub}>
							Visão geral dos atendimentos, da IA e dos bancos em tempo real
						</div>
					</div>

					<Box className={classes.filtros}>
						<PeriodFilter value={period} onChange={setPeriod} />

						<Autocomplete
							size="small"
							className={classes.formControl}
							options={opcoesConexao}
							getOptionLabel={o => (o && o.name) || ""}
							getOptionSelected={(o, v) => String(o.id) === String(v.id)}
							value={acharOpcao(opcoesConexao, connectionFilter)}
							onChange={(e, novo) => setConnectionFilter(novo ? novo.id : "all")}
							inputValue={buscaConexao}
							onInputChange={(e, texto, motivo) =>
								setBuscaConexao(motivo === "input" ? texto : "")
							}
							disableClearable
							forcePopupIcon={false}
							openOnFocus
							blurOnSelect
							classes={{
								popper: classes.popupFiltro,
								paper: classes.paperFiltro,
								option: classes.opcaoFiltro,
								noOptions: classes.semResultado,
							}}
							noOptionsText="Nenhum resultado"
							ListboxProps={{ className: classes.listaFiltro }}
							renderInput={params => (
								<TextField
									{...params}
									variant="outlined"
									placeholder={acharOpcao(opcoesConexao, connectionFilter).name}
									autoComplete="off"
									InputProps={{
										...params.InputProps,
										startAdornment: <Search className={classes.iconeBusca} />,
									}}
									inputProps={{ ...params.inputProps, autoComplete: "off" }}
								/>
							)}
						/>

						{user.profile === "admin" && (
							<Autocomplete
								size="small"
								className={classes.formControl}
								options={opcoesAtendente}
								getOptionLabel={o => (o && o.name) || ""}
								getOptionSelected={(o, v) => String(o.id) === String(v.id)}
								value={acharOpcao(opcoesAtendente, userFilter)}
								onChange={(e, novo) => setUserFilter(novo ? novo.id : "all")}
								inputValue={buscaAtendente}
								onInputChange={(e, texto, motivo) =>
									setBuscaAtendente(motivo === "input" ? texto : "")
								}
								disableClearable
								forcePopupIcon={false}
								openOnFocus
								blurOnSelect
								classes={{
									popper: classes.popupFiltro,
									paper: classes.paperFiltro,
									option: classes.opcaoFiltro,
									noOptions: classes.semResultado,
								}}
								noOptionsText="Nenhum resultado"
								ListboxProps={{ className: classes.listaFiltro }}
								renderInput={params => (
									<TextField
										{...params}
										variant="outlined"
										placeholder={acharOpcao(opcoesAtendente, userFilter).name}
										autoComplete="off"
										InputProps={{
											...params.InputProps,
											startAdornment: <Search className={classes.iconeBusca} />,
										}}
										inputProps={{ ...params.inputProps, autoComplete: "off" }}
									/>
								)}
							/>
						)}

						<Button
							variant="contained"
							disableElevation
							className={classes.clearBtn}
							startIcon={<BackspaceOutlined fontSize="small" />}
							onClick={handleClear}
						>
							Limpar
						</Button>
					</Box>
				</div>

				{[
					{ titulo: "Atendimento", itens: cardsAtendimento, colunas: 3 },
					{ titulo: "Assistente virtual", itens: cardsIA, colunas: 3 },
					{ titulo: "Bancos", itens: cardsBancos, colunas: 4 },
				].map(bloco => (
					<div key={bloco.titulo}>
						<Typography className={classes.tituloSecao}>{bloco.titulo}</Typography>
						<Grid container spacing={3} className={classes.cardGrid}>
							{bloco.itens.map((card, idx) => {
								const Icon = card.icon
								const cor = card.destaque ? "#ffffff" : card.color
								return (
									<Grid item xs={12} sm={6} md={bloco.colunas} key={idx}>
										<Paper
											className={`${classes.statCard} ${card.destaque ? classes.cardDestaque : ""}`}
											variant="outlined"
											style={{
												"--cor": card.color,
												"--brilho": hexParaRgba(card.color, escuro ? 0.16 : 0.08),
												animationDelay: `${idx * 0.06}s`,
											}}
										>
											<div className={classes.cardHeader}>
												<Typography className={classes.cardTitle}>
													{card.title}
												</Typography>
												<Box
													className={classes.cardIconBox}
													style={{
														animationDelay: `${idx * 0.5}s`,
														backgroundColor: card.destaque
															? "rgba(255,255,255,.2)"
															: hexParaRgba(card.color, escuro ? 0.14 : 0.1),
														border: `1px solid ${card.destaque ? "rgba(255,255,255,.3)" : hexParaRgba(card.color, 0.3)}`,
													}}
												>
													<Icon style={{ color: cor, fontSize: 20 }} />
												</Box>
											</div>
											<Typography className={classes.cardNumber} style={{ color: cor }}>
												{loading ? (
													<CircularProgress size={28} style={{ color: cor }} />
												) : (
													<ContadorAnimado valor={card.count} />
												)}
											</Typography>
											<Typography className={classes.cardLabel}>
												{card.label}
											</Typography>
											<Typography className={classes.cardFooter}>
												{card.footer}
											</Typography>
											<Onda
												atraso={-idx * 2.3}
												cor={
													card.destaque
														? "rgba(255,255,255,.12)"
														: hexParaRgba(card.color, escuro ? 0.07 : 0.06)
												}
											/>
										</Paper>
									</Grid>
								)
							})}
						</Grid>
					</div>
				))}

				<Typography className={classes.tituloSecao}>Movimento</Typography>
				<Grid container spacing={3} alignItems="flex-start">
					{temBancos && (
						<Grid item xs={12} md={5}>
							<Paper className={classes.rankPaper} variant="outlined">
								<Typography className={classes.paperTitle}>
									Propostas por banco
								</Typography>
								{bancos.porBanco.map(b => {
									const maior = bancos.porBanco[0]?.total || 1
									return (
										<div key={b.banco} className={classes.linhaBanco}>
											<span className={classes.nomeBanco}>{b.banco}</span>
											<div className={classes.trilhaBarra}>
												<div
													className={classes.barra}
													style={{ width: `${Math.max((b.total / maior) * 100, 4)}%` }}
												/>
											</div>
											<span className={classes.valorBanco}>{b.total}</span>
											<span className={classes.volumeBanco}>
												{b.volume
													? b.volume.toLocaleString("pt-BR", {
															style: "currency",
															currency: "BRL",
															maximumFractionDigits: 0,
														})
													: "—"}
											</span>
										</div>
									)
								})}
							</Paper>
						</Grid>
					)}

					<Grid item xs={12} md={temBancos ? 7 : 12}>
						<Paper className={classes.chartPaper} variant="outlined">
							<Chart
								serie={resumo?.serie}
								titulo={tituloGrafico}
								loading={loading}
							/>
						</Paper>
					</Grid>
				</Grid>
			</Container>
		</div>
	)
}

export default Dashboard
